import { create } from 'zustand';

/**
 * Global team-chat dock state — lets agents pop open internal channel/DM
 * conversations from ANY screen (Inbox, CRM, etc.), like Intercom/Messenger chat
 * heads. The chat windows are launched from the unified `<CommsHub/>` (mounted
 * app-wide in providers, outside routes); this store holds which conversations
 * are open. Cleared on logout.
 *
 * TC-21 multiwindow model: `openChats` is every docked conversation (oldest
 * first); `minimized` is the subset currently collapsed to a 48px chip next to
 * the launcher (Messenger pattern). Only `openChats − minimized` render as full
 * windows, capped at `maxExpandedForViewport()` — opening one more chat
 * auto-minimizes the OLDEST expanded window instead of silently discarding it
 * (the old `slice(-3)` behaviour).
 */

/** Expanded (non-minimized) window cap on wide viewports (≥1280px). */
export const MAX_EXPANDED_WIDE = 3;
/** Expanded window cap on narrow viewports (<1280px). */
export const MAX_EXPANDED_NARROW = 2;

/**
 * Viewport-aware expanded-window cap, read at ACTION time (open/restore) via
 * `window.matchMedia` — deliberately no resize listener: already-expanded
 * windows aren't force-collapsed mid-session by a viewport change; the cap
 * simply applies to the next open/restore. Falls back to the wide cap outside
 * a browser (tests / SSR).
 */
export function maxExpandedForViewport(): number {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return MAX_EXPANDED_WIDE;
  }
  return window.matchMedia('(min-width: 1280px)').matches
    ? MAX_EXPANDED_WIDE
    : MAX_EXPANDED_NARROW;
}

export interface DockLayout {
  /** Every docked conversation, by channelId (most-recently opened last). */
  openChats: string[];
  /** Subset of `openChats` collapsed to chips. */
  minimized: string[];
}

/**
 * Pure: guarantee at most `maxExpanded` expanded windows by minimizing the
 * OLDEST expanded ones first — never `keepId` (the chat the user just opened
 * or restored). Also drops stale `minimized` ids that are no longer open.
 */
export function enforceExpandedCap(
  layout: DockLayout,
  keepId: string,
  maxExpanded: number,
): DockLayout {
  const nextMin = layout.minimized.filter((id) => layout.openChats.includes(id));
  let expanded = layout.openChats.filter((id) => !nextMin.includes(id));
  while (expanded.length > Math.max(1, maxExpanded)) {
    const oldest = expanded.find((id) => id !== keepId) ?? expanded[0];
    nextMin.push(oldest);
    expanded = expanded.filter((id) => id !== oldest);
  }
  return { openChats: layout.openChats, minimized: nextMin };
}

/**
 * Pure open-chat planner. Opening an already-docked chat restores it (removes
 * it from `minimized`); opening a new one appends it. Either way the expanded
 * cap is re-enforced with the target chat protected from auto-minimize.
 */
export function planOpenChat(
  layout: DockLayout,
  channelId: string,
  maxExpanded: number,
): DockLayout {
  const openChats = layout.openChats.includes(channelId)
    ? layout.openChats
    : [...layout.openChats, channelId];
  const minimized = layout.minimized.filter((id) => id !== channelId);
  return enforceExpandedCap({ openChats, minimized }, channelId, maxExpanded);
}

interface ChatDockState extends DockLayout {
  pickerOpen: boolean;
  /**
   * TC-20d: channel the user asked to "Abrir en Chat Interno" from a dock
   * window. The dock can't reach the router (CommsHub mounts OUTSIDE
   * RouterProvider), so it stores the target here + pushes `/chat?ch=<id>`;
   * TeamChatPage consumes this (reads it, selects the channel, then calls
   * `setOpenInFullView(null)`) as its initial-channel override.
   */
  openInFullView: string | null;
  /**
   * Hilo a abrir junto con `openInFullView` — seteado cuando "Abrir en Chat
   * Interno" se pide desde el MINI-HILO del dock (parent_message_id).
   * TeamChatPage lo convierte en su `openThreadRequest` para ChannelView.
   * Viaja y se consume SIEMPRE junto al canal, nunca suelto.
   */
  openInFullViewThread: string | null;
  togglePicker: () => void;
  setPicker: (open: boolean) => void;
  openChat: (channelId: string) => void;
  closeChat: (channelId: string) => void;
  /** Collapse an open window to a chip / restore a chip to a window. */
  toggleMinimized: (channelId: string) => void;
  setOpenInFullView: (channelId: string | null, parentMessageId?: string | null) => void;
  clear: () => void;
}

export const useChatDockStore = create<ChatDockState>((set) => ({
  pickerOpen: false,
  openChats: [],
  minimized: [],
  openInFullView: null,
  openInFullViewThread: null,
  togglePicker: () => set((s) => ({ pickerOpen: !s.pickerOpen })),
  setPicker: (open) => set({ pickerOpen: open }),
  openChat: (channelId) =>
    set((s) => ({
      ...planOpenChat(s, channelId, maxExpandedForViewport()),
      pickerOpen: false,
    })),
  closeChat: (channelId) =>
    set((s) => ({
      openChats: s.openChats.filter((id) => id !== channelId),
      minimized: s.minimized.filter((id) => id !== channelId),
    })),
  toggleMinimized: (channelId) =>
    set((s) => {
      if (!s.openChats.includes(channelId)) return {};
      if (s.minimized.includes(channelId)) {
        // Restore — may push the expanded count over the cap, so re-enforce
        // it (protecting the chat being restored).
        return enforceExpandedCap(
          { openChats: s.openChats, minimized: s.minimized.filter((id) => id !== channelId) },
          channelId,
          maxExpandedForViewport(),
        );
      }
      return { minimized: [...s.minimized, channelId] };
    }),
  setOpenInFullView: (channelId, parentMessageId) =>
    set({
      openInFullView: channelId,
      // Invariante del docstring: el hilo nunca queda armado "suelto" —
      // sin canal (consume/clear) se anula aunque llegue un parentMessageId.
      openInFullViewThread: channelId ? (parentMessageId ?? null) : null,
    }),
  clear: () =>
    set({
      pickerOpen: false,
      openChats: [],
      minimized: [],
      openInFullView: null,
      openInFullViewThread: null,
    }),
}));
